import { AuthenticationError } from '@/services/api/api.errors';
import type { LoginData, SignupData, AuthResponse, TokenRefreshResponse, ProfileUpdateData } from '@/types/auth.types';
import type { User } from '@/types/user';

// Mock user database
const mockUsers: Record<string, User & { password: string }> = {
  'admin@example.com': {
    id: '1',
    email: 'admin@example.com',
    password: 'password123',
    role: 'admin',
    name: 'Admin User',
    avatar_url: null,
    is_profile_complete: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  'dev@example.com': {
    id: '2',
    email: 'dev@example.com',
    password: 'password123',
    role: 'developer',
    name: 'Dev User',
    avatar_url: null,
    is_profile_complete: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  'client@example.com': {
    id: '3',
    email: 'client@example.com',
    password: 'password123',
    role: 'client',
    name: 'Client User',
    avatar_url: null,
    is_profile_complete: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  }
};

// Store for active sessions
const activeSessions: Map<string, { userId: string; expiry: number }> = new Map();

// Generate mock JWT token
function generateMockToken(user: User, type: 'access' | 'refresh' = 'access'): string {
  const exp = type === 'access' 
    ? Date.now() / 1000 + 3600 // 1 hour for access token
    : Date.now() / 1000 + 604800; // 7 days for refresh token
    
  const payload = {
    sub: user.id,
    email: user.email,
    role: user.role,
    exp,
    iat: Date.now() / 1000
  };
  
  // Create a base64 encoded mock JWT (not cryptographically secure, just for mocking)
  const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
  const body = btoa(JSON.stringify(payload));
  const signature = btoa(`mock-signature-${Date.now()}`);
  
  return `${header}.${body}.${signature}`;
}

export const authMocks = {
  login: async (email: string, password: string): Promise<AuthResponse> => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const user = mockUsers[email];
    
    if (!user || user.password !== password) {
      throw new AuthenticationError('Invalid credentials');
    }
    
    // Remove password from response
    const { password: _, ...userWithoutPassword } = user;
    
    const accessToken = generateMockToken(userWithoutPassword);
    const refreshToken = generateMockToken(userWithoutPassword, 'refresh');
    
    // Store session
    activeSessions.set(refreshToken, {
      userId: user.id,
      expiry: Date.now() + 604800000 // 7 days
    });
    
    return {
      success: true,
      token: accessToken,
      refreshToken: refreshToken,
      user: userWithoutPassword
    };
  },
  
  signup: async (data: SignupData): Promise<AuthResponse> => {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Check if user already exists
    if (mockUsers[data.email]) {
      throw new AuthenticationError('User already exists');
    }
    
    // Create new user
    const newUser: User = {
      id: String(Object.keys(mockUsers).length + 1),
      email: data.email,
      role: data.role,
      name: data.name,
      avatar_url: null,
      is_profile_complete: false, // New users need to complete profile
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    // Store user with password
    mockUsers[data.email] = {
      ...newUser,
      password: data.password
    };
    
    const accessToken = generateMockToken(newUser);
    const refreshToken = generateMockToken(newUser, 'refresh');
    
    // Store session
    activeSessions.set(refreshToken, {
      userId: newUser.id,
      expiry: Date.now() + 604800000 // 7 days
    });
    
    return {
      success: true,
      token: accessToken,
      refreshToken: refreshToken,
      user: newUser
    };
  },
  
  refresh: async (refreshToken: string): Promise<TokenRefreshResponse> => {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const session = activeSessions.get(refreshToken);
    
    if (!session || session.expiry < Date.now()) {
      throw new AuthenticationError('Invalid or expired refresh token');
    }
    
    // Find user
    const user = Object.values(mockUsers).find(u => u.id === session.userId);
    if (!user) {
      throw new AuthenticationError('User not found');
    }
    
    const { password: _, ...userWithoutPassword } = user;
    
    // Generate new tokens
    const newAccessToken = generateMockToken(userWithoutPassword);
    const newRefreshToken = generateMockToken(userWithoutPassword, 'refresh');
    
    // Update session
    activeSessions.delete(refreshToken);
    activeSessions.set(newRefreshToken, {
      userId: user.id,
      expiry: Date.now() + 604800000 // 7 days
    });
    
    return {
      success: true,
      token: newAccessToken,
      refreshToken: newRefreshToken
    };
  },
  
  logout: async (refreshToken: string): Promise<void> => {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    // Remove session
    activeSessions.delete(refreshToken);
  },
  
  getProfile: async (userId: string): Promise<User> => {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const user = Object.values(mockUsers).find(u => u.id === userId);
    if (!user) {
      throw new AuthenticationError('User not found');
    }
    
    const { password: _, ...userWithoutPassword } = user;
    return userWithoutPassword;
  },
  
  updateProfile: async (userId: string, data: ProfileUpdateData): Promise<User> => {
    await new Promise(resolve => setTimeout(resolve, 400));
    
    const userEmail = Object.keys(mockUsers).find(email => mockUsers[email].id === userId);
    if (!userEmail) {
      throw new AuthenticationError('User not found');
    }
    
    const user = mockUsers[userEmail];
    
    // Update user data
    const updatedUser = {
      ...user,
      ...data,
      is_profile_complete: true, // Mark profile as complete after update
      updated_at: new Date().toISOString()
    };
    
    mockUsers[userEmail] = updatedUser;
    
    const { password: _, ...userWithoutPassword } = updatedUser;
    return userWithoutPassword;
  }
};

// Helper to extract user ID from mock token
export function extractUserIdFromToken(token: string): string | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    const payload = JSON.parse(atob(parts[1]));
    return payload.sub || null;
  } catch {
    return null;
  }
}